import type { api, IslandNameStructure } from '@guild/core'

import type { App } from './app.ts'
import { createStore } from './store.ts'

/**
 * The notices from the Keeper of the Rolls (Q32.2, #141).
 *
 * The morning an island goes under, its finished card -- relics drawn, lies
 * caught, the chart coloured -- arrives in the post as a notice, one per
 * finalised island the player walked, for seven days; after that the card is
 * simply in the Atlas. Nothing new is stored on the server: a notice is an
 * island the Atlas already lists, read off it with today's day, and whether
 * it has been opened is a reading mark rather than a fact about the game, so
 * it is the client's to remember -- `localStorage`, per player, best-effort.
 *
 * The store is filled wherever the Atlas is fetched with today in hand (the
 * day store on boot, the post on arrival), and cleared with the rest of a
 * player's state when the player changes (#115). The mark beside `[ The Post ]`
 * and the course strip's first step read it, as they read the inbox.
 */

export type Notice = {
  readonly day: string
  readonly dayNumber: number | null
  readonly islandName: IslandNameStructure
  /** The Atlas thumbnail of the island, drawn on the opened notice's card. */
  readonly thumbnail: string
  readonly opened: boolean
}

export type NoticeState = {
  notices: readonly Notice[]
  loaded: boolean
}

export const noticeStore = createStore<NoticeState>({ notices: [], loaded: false })

/** How many mornings a notice stays in the post after the island goes under. */
export const NOTICE_DAYS = 7

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole UTC days from one game day to another. */
const daysApart = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS)

/**
 * The islands with a notice due: finalised, and finalised within the last
 * seven days. An island of day N goes under at 00:00 of N + 2, so its notice
 * is in the post from that morning for seven mornings.
 */
export function noticesFrom(
  islands: readonly api.ApiAtlas['islands'][number][],
  today: string,
  opened: ReadonlySet<string>,
): Notice[] {
  const due: Notice[] = []
  for (const island of islands) {
    if (island.status !== 'finalised') continue
    const since = daysApart(island.day, today) - 2
    if (since < 0 || since >= NOTICE_DAYS) continue
    due.push({
      day: island.day,
      dayNumber: island.dayNumber,
      islandName: island.islandName,
      thumbnail: island.thumbnail,
      opened: opened.has(island.day),
    })
  }
  // Newest first, whatever order the Atlas came in.
  return due.sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))
}

/** Read the Atlas for notices, with today from the server's island. */
export function noteAtlas(app: App, atlas: api.ApiAtlas, today: string): void {
  noticeStore.set({ notices: noticesFrom(atlas.islands, today, openedDays(app)), loaded: true })
}

export const unopenedNotices = (): number =>
  noticeStore.get().notices.filter((notice) => !notice.opened).length

export const hasNotices = (): boolean => noticeStore.get().notices.length > 0

/** Break the seal: remembered for this player, and reflected at once. */
export function openNotice(app: App, day: string): void {
  const days = openedDays(app)
  days.add(day)
  rememberOpened(app, days)
  noticeStore.set({
    notices: noticeStore
      .get()
      .notices.map((notice) => (notice.day === day ? { ...notice, opened: true } : notice)),
  })
}

/** Forget the notices when the player changes: this post is not the next one's. */
export function clearNotices(): void {
  noticeStore.reset()
}

// --- what is remembered ------------------------------------------------------

/**
 * Keyed by the player's name, which is the one identity the client holds
 * (`ApiMe` carries no id), so another account on the same browser finds its
 * own marks and not this player's.
 */
const storageKey = (app: App): string | null =>
  app.user === null ? null : `guild.notices.${app.user.username.toLowerCase()}`

/** The days whose notice this player has opened. Best-effort: storage may refuse. */
function openedDays(app: App): Set<string> {
  const key = storageKey(app)
  if (key === null) return new Set()
  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(key) ?? '[]')
    return new Set(Array.isArray(stored) ? stored.filter((day) => typeof day === 'string') : [])
  } catch {
    return new Set()
  }
}

function rememberOpened(app: App, days: ReadonlySet<string>): void {
  const key = storageKey(app)
  if (key === null) return
  // Only the days with a notice still in the post: a notice is gone after
  // seven mornings, so nothing older is worth keeping.
  const showing = new Set(noticeStore.get().notices.map((notice) => notice.day))
  const kept = [...days].filter((day) => showing.has(day)).sort()
  try {
    window.localStorage.setItem(key, JSON.stringify(kept))
  } catch {
    // A mark that cannot be remembered is still a mark for this visit.
  }
}
